import React, { useMemo, useState } from 'react';
import { FileDown, Mail, Share2 } from 'lucide-react';
import { useReceipts, useRoutes, useSpendSummary, useTrip } from '../hooks/useTravelBuddy';
import {
  buildSummaryPdfBlob,
  downloadBlob,
  downloadSummaryPdf,
  exportTripReceiptsPdfBlob,
  safeFilePart,
} from '../lib/tripExportPdf';
import { canSharePdfFile, openEmailComposerForPdf, sharePdfFile } from '../lib/pdfShare';
import { RECEIPT_CATEGORY_LABELS } from '../lib/categoryLabels';
import {
  compareReceiptsForSummary,
  formatReceiptSummaryLine,
  routeLegLabelsByReceiptId,
} from '../lib/receiptSummaryLines';
import type { ReceiptCategory } from '../types';
import * as T from '../ui/inlineTheme';

const exportBtn: React.CSSProperties = {
  ...T.btnSecondary,
  width: '100%',
  justifyContent: 'center',
};

const exportBtnRow: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

export function SummaryTab({ tripId }: { tripId: string }) {
  const { data, isLoading } = useSpendSummary(tripId);
  const { data: trip, isLoading: tripLoading } = useTrip(tripId);
  const { data: receipts = [], isLoading: receiptsLoading } = useReceipts(tripId);
  const { data: routes = [], isLoading: routesLoading } = useRoutes(tripId);
  const [shareBusy, setShareBusy] = useState<'receipts' | 'summary' | null>(null);

  const tripName = trip?.name ?? 'Trip';

  const legByReceiptId = useMemo(() => routeLegLabelsByReceiptId(routes), [routes]);

  /** Any uploaded file with “Receipts PDF” enabled */
  const receiptsForPdf = useMemo(
    () => receipts.filter((r) => r.include_in_receipts_pdf !== false),
    [receipts]
  );

  /** Included in summary list & summary PDF */
  const receiptsForSummary = useMemo(
    () => receipts.filter((r) => r.include_in_summary !== false),
    [receipts]
  );

  const sortedReceiptLines = useMemo(() => {
    return [...receiptsForSummary].sort(compareReceiptsForSummary).map((r) => ({
      id: r.id,
      text: formatReceiptSummaryLine(r, legByReceiptId),
    }));
  }, [receiptsForSummary, legByReceiptId]);

  const onReceiptsPdf = () => {
    if (receiptsForPdf.length === 0) {
      window.alert(
        'No files selected for the receipts PDF. Turn on “Receipts PDF” on the Receipts tab for each file you want embedded.'
      );
      return;
    }
    void exportTripReceiptsPdfBlob(tripName, receiptsForPdf).then((out) => {
      if (out) downloadBlob(out.blob, out.filename);
    });
  };

  const onShareReceiptsPdf = async () => {
    if (receiptsForPdf.length === 0) {
      window.alert('No receipts selected for export. Enable “Receipts PDF” on the receipts you want.');
      return;
    }
    setShareBusy('receipts');
    try {
      const out = await exportTripReceiptsPdfBlob(tripName, receiptsForPdf);
      if (!out) {
        window.alert('Could not build a receipts PDF (no embedded files).');
        return;
      }
      if (canSharePdfFile(out.blob, out.filename)) {
        try {
          await sharePdfFile(out.blob, out.filename, `${tripName} — receipts`);
          return;
        } catch (e) {
          console.warn('[Travel Buddy] Share cancelled or failed', e);
        }
      }
      downloadBlob(out.blob, out.filename);
      openEmailComposerForPdf(`${tripName} — receipts`, out.filename);
    } finally {
      setShareBusy(null);
    }
  };

  const onSummaryPdf = () => {
    if (!data) return;
    downloadSummaryPdf(tripName, data, receiptsForSummary, routes);
  };

  const onShareSummaryPdf = async () => {
    if (!data) return;
    setShareBusy('summary');
    try {
      const safeName = `${safeFilePart(tripName)}-summary.pdf`;
      const blob = buildSummaryPdfBlob(tripName, data, receiptsForSummary, routes);
      if (canSharePdfFile(blob, safeName)) {
        try {
          await sharePdfFile(blob, safeName, `${tripName} — summary`);
          return;
        } catch (e) {
          console.warn('[Travel Buddy] Share cancelled or failed', e);
        }
      }
      downloadBlob(blob, safeName);
      openEmailComposerForPdf(`${tripName} — summary`, safeName);
    } finally {
      setShareBusy(null);
    }
  };

  if (isLoading || !data || receiptsLoading || tripLoading || routesLoading) {
    return <p style={T.bodyMuted}>Calculating…</p>;
  }

  const entries = (Object.keys(data.byCategory) as ReceiptCategory[]).filter(
    (k) => data.byCategory[k] > 0
  );

  return (
    <div style={T.flexGap}>
      <div style={T.cardSolid}>
        <p style={{ ...T.labelSmall, marginBottom: 12 }}>By receipt</p>
        {sortedReceiptLines.length === 0 ? (
          <p style={{ ...T.bodyMuted, margin: 0 }}>
            No receipts included in the summary. Turn on “Summary” on each file on the Receipts tab, or add receipts.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {sortedReceiptLines.map(({ id, text }, idx) => (
              <li
                key={id}
                style={{
                  fontFamily: T.font,
                  fontSize: 15,
                  padding: '8px 0',
                  color: '#d4d4d8',
                  lineHeight: 1.45,
                  borderBottom: idx < sortedReceiptLines.length - 1 ? '1px solid #27272a' : undefined,
                }}
              >
                {text}
              </li>
            ))}
          </ul>
        )}
        <p style={{ ...T.bodyMuted, margin: '12px 0 0', fontSize: 13 }}>
          Flights, trains, buses, and ferries use <strong style={{ color: '#a1a1aa' }}>origin — destination</strong>{' '}
          when a matching route is linked (same upload or Routes tab). Other lines use the detected merchant or file
          name. Use per-receipt toggles on the Receipts tab to omit items from this list or from the PDFs.
        </p>
      </div>

      <div style={T.cardSolid}>
        <p style={{ ...T.labelSmall, marginBottom: 12 }}>Spend by category</p>
        {entries.length === 0 ? (
          <p style={{ ...T.bodyMuted, margin: 0 }}>No receipt amounts yet (or all receipts excluded from summary).</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {entries.map((k) => (
              <li
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontFamily: T.font,
                  fontSize: 15,
                  padding: '8px 0',
                  color: '#d4d4d8',
                }}
              >
                <span>{RECEIPT_CATEGORY_LABELS[k]}</span>
                <span style={{ fontWeight: 600, color: '#fafafa' }}>
                  {data.byCategory[k].toFixed(2)} {data.currency}
                </span>
              </li>
            ))}
            <li
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
                paddingTop: 12,
                borderTop: '1px solid #3f3f46',
                fontFamily: T.font,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <span style={{ color: '#e4e4e7' }}>Total</span>
              <span style={{ color: '#3A8DFF' }}>
                {data.total.toFixed(2)} {data.currency}
              </span>
            </li>
          </ul>
        )}
      </div>

      <div style={exportBtnRow}>
        <div style={exportBtnRow}>
          <button type="button" onClick={onReceiptsPdf} style={exportBtn}>
            <FileDown size={20} />
            Download receipts PDF
          </button>
          <button
            type="button"
            onClick={() => void onShareReceiptsPdf()}
            disabled={shareBusy !== null}
            style={{
              ...exportBtn,
              ...T.btnSecondary,
              opacity: shareBusy !== null ? 0.7 : 1,
            }}
          >
            <Share2 size={20} />
            {shareBusy === 'receipts' ? 'Preparing…' : 'Share or email receipts PDF'}
          </button>
          <button
            type="button"
            onClick={() => {
              const subj = encodeURIComponent(`${tripName} — receipts`);
              window.open(`mailto:?subject=${subj}`, '_blank', 'noopener,noreferrer');
            }}
            style={{
              ...exportBtn,
              borderStyle: 'dashed',
            }}
          >
            <Mail size={20} />
            Open email (attach file manually)
          </button>
        </div>
        <p style={{ ...T.bodyMuted, margin: 0, fontSize: 13 }}>
          Every upload can be embedded when <strong style={{ color: '#a1a1aa' }}>Receipts PDF</strong> is on — expenses
          and travel tickets alike.
        </p>
      </div>

      <div style={exportBtnRow}>
        <button type="button" onClick={onSummaryPdf} style={exportBtn}>
          <FileDown size={20} />
          Download summary PDF
        </button>
        <button
          type="button"
          onClick={() => void onShareSummaryPdf()}
          disabled={shareBusy !== null}
          style={{
            ...exportBtn,
            opacity: shareBusy !== null ? 0.7 : 1,
          }}
        >
          <Share2 size={20} />
          {shareBusy === 'summary' ? 'Preparing…' : 'Share or email summary PDF'}
        </button>
        <p style={{ ...T.bodyMuted, margin: 0, fontSize: 13 }}>
          <strong>Share</strong> uses the device share sheet when available (often includes Mail on phones). Otherwise the
          file downloads and your email app opens with a reminder to attach it.
        </p>
      </div>
    </div>
  );
}
